import React, { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Approval as ApprovalsIcon } from '@mui/icons-material';
import { useAppSelector } from '../../../../app/store';
import { WriteoffApprovalView } from '../../../../services/WriteoffService/types/writeoffTypes';
import SectionPaper from '../../../common/components/styled-ui-elements/SectionPaper';
import { ApprovalDialog } from '../dialogs/ApprovalDialog';
import ApprovalsGrid from '../grids/ApprovalsGrid';


// Empty state component
const EmptyApprovals: React.FC = () => (
  <Box sx={{ textAlign: 'center', py: 4 }}>
    <Typography variant="body1" sx={{ color: '#64748b', mb: 1 }}>
      No approval levels configured
    </Typography>
    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
      Approval levels will be automatically added when the writeoff is submitted based on center configuration.
    </Typography>
  </Box>
);

// Main component
export const ApprovalsSection: React.FC = () => {
  const { currentWriteoff } = useAppSelector(state => state.writeoff);
  const [selectedApproval, setSelectedApproval] = useState<WriteoffApprovalView | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  
  const approvals = currentWriteoff?.approvals || [];
  const hasApprovals = approvals.length > 0;
  const isInApprovalStatus = currentWriteoff?.status?.toUpperCase() === 'SUBMITTED';

  const handleApprovalAction = (approval: WriteoffApprovalView, readOnly: boolean = false) => {
    setSelectedApproval(approval);
    setIsReadOnly(readOnly);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedApproval(null);
    setIsReadOnly(false);
  };

  return (
    <>
      {/* Header Section */}
      <SectionPaper sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap={1.5}>
            <ApprovalsIcon sx={{ color: '#3b82f6', fontSize: 24 }} />
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1e293b',
                  mb: 0.5
                }}
              >
                Approval Levels
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: '#64748b' }}
              >
                {approvals.length === 0 
                  ? 'No approval levels configured for this writeoff' 
                  : isInApprovalStatus
                    ? `${approvals.length} approval level${approvals.length !== 1 ? 's' : ''} - Actions available`
                    : `${approvals.length} approval level${approvals.length !== 1 ? 's' : ''} - View only`
                }
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </SectionPaper>

      {/* Approvals Grid Section */}
      {!hasApprovals ? (
        <SectionPaper>
          <EmptyApprovals />
        </SectionPaper>
      ) : (
        <SectionPaper sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, overflow: 'hidden' }}>
          <ApprovalsGrid
            approvals={approvals}
            isInApprovalStatus={isInApprovalStatus}
            onApprovalAction={handleApprovalAction}
          />
        </SectionPaper>
      )}

      {/* Approval Dialog */}
      {selectedApproval && currentWriteoff && (
        <ApprovalDialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          approval={selectedApproval}
          writeoffId={currentWriteoff.identifiers.id}
          company={currentWriteoff.identifiers.company}
          center={currentWriteoff.identifiers.center}
          writeoffAmount={currentWriteoff.detail.writeoffAmount}
          readOnly={isReadOnly}
        />
      )}
    </>
  );
};

export default ApprovalsSection;
